import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import LanguageSelector from '@/components/LanguageSelector';
import { Layers, Sun, Moon, ArrowLeft, Lock, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenError('missing');
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/auth/verify-reset-token/${token}`);
        if (response.data.valid) {
          setTokenValid(true);
          setUserEmail(response.data.email);
        } else {
          setTokenError(response.data.reason || 'invalid');
        }
      } catch (error) {
        setTokenError('invalid');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error(t('auth.passwordsDoNotMatch'));
      return;
    }
    
    // Validate password length
    if (formData.newPassword.length < 6) {
      toast.error(t('auth.passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token: token,
        new_password: formData.newPassword
      });
      
      setResetSuccess(true);
      toast.success(t('auth.passwordResetSuccess'));
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/auth');
      }, 3000);
    } catch (error) {
      const message = error.response?.data?.detail || t('auth.resetError');
      toast.error(message);
      
      // If token is expired or invalid, show the error state
      if (error.response?.status === 400) {
        setTokenValid(false);
        setTokenError('expired');
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state while verifying token
  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-muted-foreground">{t('auth.verifyingToken')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 py-12">
        <div className="w-full max-w-md mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-12">
            <Link to="/auth" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">{t('auth.backToLogin')}</span>
            </Link>
            <div className="flex items-center gap-2">
              <LanguageSelector variant="compact" />
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="reset-theme-toggle">
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-foreground rounded-xl flex items-center justify-center">
              <Layers className="w-6 h-6 text-background" />
            </div>
            <span className="font-semibold text-2xl tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              MockupPro
            </span>
          </div>

          {/* Token Error State */}
          {!tokenValid && tokenError && !resetSuccess && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h1 
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {tokenError === 'missing' ? t('auth.tokenMissing') : 
                 tokenError === 'expired' ? t('auth.tokenExpired') : 
                 t('auth.tokenInvalid')}
              </h1>
              <p className="text-muted-foreground mb-6">
                {tokenError === 'missing' ? t('auth.tokenMissingInfo') :
                 tokenError === 'expired' ? t('auth.tokenExpiredInfo') :
                 t('auth.tokenInvalidInfo')}
              </p>
              
              <div className="flex gap-3 justify-center">
                <Link to="/forgot-password">
                  <Button variant="default">
                    {t('auth.requestNewLink')}
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button variant="outline">
                    {t('auth.backToLogin')}
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}

          {/* Reset Success State */}
          {resetSuccess && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h1 
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {t('auth.passwordResetComplete')}
              </h1>
              <p className="text-muted-foreground mb-6">
                {t('auth.passwordResetCompleteInfo')}
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                {t('auth.redirectingToLogin')}
              </p>
              <Link to="/auth">
                <Button variant="default">
                  {t('auth.signInNow')}
                </Button>
              </Link>
            </motion.div>
          )}

          {/* Reset Password Form */}
          {tokenValid && !resetSuccess && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h1 
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="reset-heading"
              >
                {t('auth.resetPassword')}
              </h1>
              <p className="text-muted-foreground mb-2">
                {t('auth.resetPasswordInfo')}
              </p>
              {userEmail && (
                <p className="text-sm text-muted-foreground mb-8">
                  {t('auth.resettingFor')}: <strong>{userEmail}</strong>
                </p>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">{t('auth.newPassword')}</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.newPassword}
                      onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">{t('auth.passwordRequirements')}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required
                      minLength={6}
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {formData.newPassword && formData.confirmPassword && 
                 formData.newPassword !== formData.confirmPassword && (
                  <p className="text-sm text-red-500">{t('auth.passwordsDoNotMatch')}</p>
                )}

                <Button 
                  type="submit" 
                  className="w-full rounded-lg" 
                  disabled={loading || formData.newPassword !== formData.confirmPassword}
                  data-testid="reset-submit"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="spinner" /> {t('auth.processing')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      {t('auth.resetPasswordBtn')}
                    </span>
                  )}
                </Button>
              </form>
            </motion.div>
          )}
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:flex flex-1 bg-muted/30 items-center justify-center p-12">
        <div className="max-w-md text-center">
          <div className="w-24 h-24 bg-accent rounded-3xl flex items-center justify-center mx-auto mb-8">
            <Lock className="w-12 h-12" />
          </div>
          <h2 
            className="text-2xl font-bold mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {t('auth.secureReset')}
          </h2>
          <p className="text-muted-foreground">
            {t('auth.secureResetInfo')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
